import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Drawer,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Chip,
  Avatar,
  InputAdornment,
  Stack,
  Divider,
  CircularProgress,
  Paper,
} from "@mui/material";

// --- ICONS ---
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  TableChart as TableChartIcon, // Google Sheets
  Facebook as FacebookIcon, // Meta
  Business as BusinessIcon, // Bitrix (Generic CRM icon)
  Refresh as RefreshIcon,
} from "@mui/icons-material";

// --- MOCK DATA ---
const MOCK_INTEGRATIONS = [
  {
    id: "google-sheets",
    title: "Google Sheets",
    description:
      "Sync your Justtawk leads directly to spreadsheet rows in real-time.",
    category: "Data",
    icon: <TableChartIcon sx={{ fontSize: 40, color: "#34A853" }} />,
    status: "disconnected",
  },
  {
    id: "meta-facebook",
    title: "Meta / Facebook",
    description: "Capture leads from Facebook & Instagram Instant Forms.",
    category: "Social",
    icon: <FacebookIcon sx={{ fontSize: 40, color: "#1877F2" }} />,
    status: "disconnected",
  },
  {
    id: "bitrix24",
    title: "Bitrix24",
    description:
      "Bi-directional sync of Deals, Contacts, and User responsibilities.",
    category: "CRM",
    icon: <BusinessIcon sx={{ fontSize: 40, color: "#00AEEF" }} />,
    status: "connected",
  },
];

// --- COMPONENT: CONFIGURATION DRAWER ---
const ConfigDrawer = ({ isOpen, onClose, integration, onSaveStatus }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [mappings, setMappings] = useState([
    { id: 1, remote: "TITLE", local: "dealName" },
    { id: 2, remote: "PHONE", local: "mobile" },
  ]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTestConnection = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };

  const addMappingRow = () => {
    setMappings([...mappings, { id: Date.now(), remote: "", local: "" }]);
  };

  const removeMappingRow = (id) => {
    setMappings(mappings.filter((m) => m.id !== id));
  };

  if (!integration) return null;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 500 } } }} // Responsive width
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#f9fafb",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: "white",
              border: "1px solid #e0e0e0",
              width: 48,
              height: 48,
            }}
          >
            {integration.icon}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
              {integration.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              Configuration
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="config tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Credentials" />
          <Tab label="Field Mapping" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {/* Body Content */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        {/* TAB 1: CREDENTIALS */}
        {activeTab === 0 && (
          <Stack spacing={3}>
            <TextField
              label="Bitrix Webhook URL"
              fullWidth
              variant="outlined"
              defaultValue="https://b24-nh0vik.bitrix24.com/rest/1144/jx1bri0ng63r4ac9/"
              placeholder="https://your-domain.bitrix24.com/rest/..."
              helperText="The inbound webhook URL from Bitrix24"
            />
            <TextField
              label="User ID (Responsible Person)"
              fullWidth
              variant="outlined"
              defaultValue="6870a9768eb8298764f058e0"
              placeholder="e.g. 1"
            />

            <Divider sx={{ my: 2 }} />

            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <RefreshIcon />
              }
              onClick={handleTestConnection}
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderColor: "#e2e8f0",
                color: "#475569",
                "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
              }}
            >
              {isLoading ? "Verifying..." : "Test Connection"}
            </Button>
          </Stack>
        )}

        {/* TAB 2: FIELD MAPPING */}
        {activeTab === 1 && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 2, px: 1 }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Remote Field (Bitrix)
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Local Field (Justtawk)
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {mappings.map((map) => (
                <Stack
                  key={map.id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Remote Key (e.g. TITLE)"
                    defaultValue={map.remote}
                  />
                  <LinkIcon sx={{ color: "#94a3b8" }} fontSize="small" />
                  <FormControl size="small" fullWidth>
                    <Select defaultValue={map.local || "dealName"}>
                      <MenuItem value="dealName">Deal Name</MenuItem>
                      <MenuItem value="mobile">Mobile</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                      <MenuItem value="stage">Stage ID</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => removeMappingRow(map.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Button
              startIcon={<AddIcon />}
              onClick={addMappingRow}
              sx={{ mt: 3, fontWeight: 600 }}
              color="primary"
            >
              Add New Mapping
            </Button>
          </Box>
        )}

        {/* TAB 3: SETTINGS */}
        {activeTab === 2 && (
          <Stack spacing={3}>
            {[
              {
                label: "Auto-sync on new lead",
                desc: "Automatically import leads when created in Bitrix.",
                active: true,
              },
              {
                label: "Two-way synchronization",
                desc: "Push Justtawk updates back to Bitrix.",
                active: false,
              },
              {
                label: "Debug Mode",
                desc: "Log raw API responses to the developer console.",
                active: true,
              },
            ].map((setting, idx) => (
              <Stack
                key={idx}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {setting.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {setting.desc}
                  </Typography>
                </Box>
                <Switch defaultChecked={setting.active} color="primary" />
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid #eee",
          bgcolor: "#f9fafb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => {
            onSaveStatus(integration.id, "connected");
            onClose();
          }}
          sx={{ bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca" } }}
        >
          Save Changes
        </Button>
      </Box>
    </Drawer>
  );
};

// --- MAIN COMPONENT: INTEGRATIONS HUB ---
const IntegrationsHubMUI = () => {
  const [filter, setFilter] = useState("All");
  const [integrations, setIntegrations] = useState(MOCK_INTEGRATIONS);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const categories = ["All", "Data", "Social", "CRM", "Utilities"];

  const handleConnectToggle = (id, newStatus) => {
    setIntegrations((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: newStatus } : item
      )
    );
  };

  const filteredList =
    filter === "All"
      ? integrations
      : integrations.filter((i) => i.category === filter);

  return (
    <Box sx={{ minHeight: "100%", py: 2 }}>
      <Container maxWidth="xl">
        {/* Page Header */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h4" sx={{ color: "#1e293b", mb: 1 }}>
            Integrations Hub
          </Typography>
          <Typography variant="body1" sx={{ color: "#64748b" }}>
            Manage your connections with third-party tools and services.
          </Typography>
        </Box>

        {/* Grid Layout */}
        <Grid container spacing={3}>
          {filteredList.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  border: "1px solid #F2F2F2",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 2 }}
                  >
                    <Box sx={{ p: 1.5, bgcolor: "#f1f5f9", borderRadius: 2 }}>
                      {item.icon}
                    </Box>
                    <Chip
                      label={item.status}
                      size="small"
                      color={
                        item.status === "connected" ? "success" : "default"
                      }
                      variant={
                        item.status === "connected" ? "filled" : "outlined"
                      }
                      sx={{ textTransform: "capitalize", fontWeight: 600 }}
                    />
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 1, color: "#1e293b" }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", lineHeight: 1.6 }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>

                <Box sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={
                      item.status === "connected" ? "outlined" : "contained"
                    }
                    color={item.status === "connected" ? "inherit" : "primary"}
                    startIcon={
                      item.status === "connected" ? (
                        <SettingsIcon />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    onClick={() => setSelectedIntegration(item)}
                    sx={{
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "none",
                      ...(item.status === "connected" && {
                        borderColor: "#e2e8f0",
                        color: "#475569",
                      }),
                      ...(item.status !== "connected" && {
                        bgcolor: "#4f46e5",
                        "&:hover": { bgcolor: "#4338ca" },
                      }),
                    }}
                  >
                    {item.status === "connected"
                      ? "Manage Settings"
                      : "Connect App"}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Configuration Drawer */}
        <ConfigDrawer
          isOpen={!!selectedIntegration}
          onClose={() => setSelectedIntegration(null)}
          integration={selectedIntegration}
          onSaveStatus={handleConnectToggle}
        />
      </Container>
    </Box>
  );
};

export default IntegrationsHubMUI;
